import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './BusinessAssets.css';

const emptyForm = {
  assetName: '',
  category: '',
  description: '',
  purchaseDate: '',
  purchasePrice: '',
  currentValue: '',
  location: '',
  depreciationRate: '',
};

const formatMoney = (value) => `Tk ${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value)).replace(/ /g, '-');
};

const toDateInput = (value) => {
  if (!value) {
    return '';
  }

  return new Date(value).toISOString().split('T')[0];
};

function BusinessAssets({ onLogout }) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [form, setForm] = useState(emptyForm);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.get('/api/business-assets', {
        ...auth,
        params: {
          search: searchTerm,
          category: categoryFilter,
        },
      });
      setAssets(response.data.assets || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to load business assets.');
    } finally {
      setLoading(false);
    }
  }, [auth, categoryFilter, searchTerm]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setEditingAssetId(null);
    setForm(emptyForm);
    setMessage('');
  };

  const submitAsset = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      if (editingAssetId) {
        await axios.put(`/api/business-assets/${editingAssetId}`, form, auth);
        setMessage('Business asset updated.');
      } else {
        await axios.post('/api/business-assets', form, auth);
        setMessage('Business asset saved.');
      }

      resetForm();
      await fetchAssets();
    } catch (error) {
      const validationMessage = error.response?.data?.errors?.[0]?.msg;
      setMessage(validationMessage || error.response?.data?.message || 'Failed to save business asset.');
    } finally {
      setSaving(false);
    }
  };

  const editAsset = (asset) => {
    setEditingAssetId(asset.AssetId);
    setForm({
      assetName: asset.AssetName || '',
      category: asset.Category || '',
      description: asset.Description || '',
      purchaseDate: toDateInput(asset.PurchaseDate),
      purchasePrice: asset.PurchasePrice ?? '',
      currentValue: asset.CurrentValue ?? '',
      location: asset.Location || '',
      depreciationRate: asset.DepreciationRate ?? '',
    });
    setMessage('');
  };

  const deleteAsset = async (assetId) => {
    const confirmed = window.confirm('Delete this business asset?');
    if (!confirmed) {
      return;
    }

    setMessage('');

    try {
      await axios.delete(`/api/business-assets/${assetId}`, auth);
      if (editingAssetId === assetId) {
        resetForm();
      }
      await fetchAssets();
      setMessage('Business asset deleted.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to delete business asset.');
    }
  };

  const totals = assets.reduce(
    (summary, asset) => ({
      purchase: summary.purchase + Number(asset.PurchasePrice || 0),
      current: summary.current + Number(asset.CurrentValue || 0),
    }),
    { purchase: 0, current: 0 }
  );

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="business-assets-page">
        <header className="business-assets-header">
          <div className="business-assets-title-group">
            <button className="app-back-button" type="button" onClick={() => navigate('/inventory')} aria-label="Back to inventory">
              Back
            </button>
            <h1>Business Assets</h1>
          </div>
          <button className="business-assets-refresh" type="button" onClick={fetchAssets}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </header>

        <section className="business-assets-summary">
          <div>
            <span>Total Assets</span>
            <strong>{assets.length}</strong>
          </div>
          <div>
            <span>Purchase Value</span>
            <strong>{formatMoney(totals.purchase)}</strong>
          </div>
          <div>
            <span>Current Value</span>
            <strong>{formatMoney(totals.current)}</strong>
          </div>
        </section>

        <section className="business-assets-workspace">
          <form className="business-assets-form" onSubmit={submitAsset}>
            <h2>{editingAssetId ? 'Edit Asset' : 'Add Asset'}</h2>

            <label>
              Asset Name
              <input value={form.assetName} onChange={(event) => updateForm('assetName', event.target.value)} required />
            </label>

            <div className="business-assets-form-grid">
              <label>
                Category
                <input value={form.category} onChange={(event) => updateForm('category', event.target.value)} placeholder="Tools, Equipment, Office" />
              </label>
              <label>
                Location
                <input value={form.location} onChange={(event) => updateForm('location', event.target.value)} placeholder="Workshop, Store, Office" />
              </label>
              <label>
                Purchase Date
                <input type="date" value={form.purchaseDate} onChange={(event) => updateForm('purchaseDate', event.target.value)} />
              </label>
              <label>
                Depreciation %
                <input type="number" min="0" step="0.01" value={form.depreciationRate} onChange={(event) => updateForm('depreciationRate', event.target.value)} />
              </label>
              <label>
                Purchase Price
                <input type="number" min="0" step="0.01" value={form.purchasePrice} onChange={(event) => updateForm('purchasePrice', event.target.value)} />
              </label>
              <label>
                Current Value
                <input type="number" min="0" step="0.01" value={form.currentValue} onChange={(event) => updateForm('currentValue', event.target.value)} />
              </label>
            </div>

            <label>
              Description
              <textarea value={form.description} onChange={(event) => updateForm('description', event.target.value)} />
            </label>

            <div className="business-assets-form-actions">
              <button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingAssetId ? 'Update Asset' : 'Save Asset'}
              </button>
              {editingAssetId && (
                <button type="button" className="secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          <section className="business-assets-list">
            <div className="business-assets-toolbar">
              <input
                type="search"
                placeholder="Search assets, category, location"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
                <option value="All">All categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div className="business-assets-table-scroll">
              <table className="business-assets-table">
                <thead>
                  <tr>
                    <th>Asset Name</th>
                    <th>Category</th>
                    <th>Location</th>
                    <th>Purchase Date</th>
                    <th>Purchase Price</th>
                    <th>Current Value</th>
                    <th>Dep. %</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr key={asset.AssetId}>
                      <td>
                        <strong>{asset.AssetName}</strong>
                        <span>{asset.Description || ''}</span>
                      </td>
                      <td>{asset.Category || '-'}</td>
                      <td>{asset.Location || '-'}</td>
                      <td>{formatDate(asset.PurchaseDate)}</td>
                      <td>{formatMoney(asset.PurchasePrice)}</td>
                      <td>{formatMoney(asset.CurrentValue)}</td>
                      <td>{asset.DepreciationRate != null ? `${Number(asset.DepreciationRate).toFixed(2)}%` : '-'}</td>
                      <td>
                        <div className="business-assets-row-actions">
                          <button type="button" onClick={() => editAsset(asset)}>Edit</button>
                          <button type="button" className="danger" onClick={() => deleteAsset(asset.AssetId)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {assets.length === 0 && (
                    <tr>
                      <td colSpan="8" className="business-assets-empty">
                        {loading ? 'Loading business assets...' : 'No business assets found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </section>

        {message && <div className="business-assets-message">{message}</div>}
      </main>
    </div>
  );
}

export default BusinessAssets;
